import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEdit, 
  faTrashAlt, 
  faToggleOn, 
  faToggleOff, 
  faChartLine, 
  faCheck, 
  faUsers, 
  faMoneyBillWave, 
  faSync, 
  faClock
} from '@fortawesome/free-solid-svg-icons';
import adminService from '../../services/adminService';

function ViewMembershipPlan() {
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [plan, setPlan] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [stats, setStats] = useState({
    activeMembers: 0,
    revenue: 0,
    averageRenewalRate: 0
  });
  
  useEffect(() => {
    fetchPlan();
    fetchPlanStats();
  }, [id]);
  
  const fetchPlan = async () => {
    setIsLoading(true);
    try {
      const data = await adminService.getMembershipPlan(id);
      setPlan(data);
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching plan:', error);
      setError('Failed to load membership plan');
      setIsLoading(false);
    }
  };
  
  const fetchPlanStats = async () => {
    try {
      const data = await adminService.getMembershipPlanStats(id);
      setStats(data);
    } catch (error) {
      console.error('Error fetching plan statistics:', error);
    }
  };
  
  const handleDelete = () => {
    setShowDeleteModal(true);
  };
  
  const confirmDelete = async () => {
    try {
      await adminService.deleteMembershipPlan(id);
      navigate('/admin/membership-plans');
    } catch (error) {
      console.error('Error deleting plan:', error);
    }
  };
  
  const cancelDelete = () => {
    setShowDeleteModal(false);
  };
  
  const togglePlanStatus = async () => {
    try {
      const response = await adminService.toggleMembershipPlanStatus(id);
      setPlan({
        ...plan,
        status: response.status
      });
    } catch (error) {
      console.error('Error toggling plan status:', error);
    }
  };
  
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };
  
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <div className="admin-loading">Loading...</div>
      </AdminLayout>
    );
  }

  if (error || !plan) {
    return (
      <AdminLayout>
        <div className="admin-error">
          Error: {error || 'Membership plan not found'}
          <button 
            className="admin-btn admin-btn-primary" 
            onClick={() => navigate('/admin/membership-plans')}
            style={{ marginTop: '1rem' }}
          >
            Back to Membership Plans
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1 className="admin-page-title">Membership Plan Details</h1>
        <ul className="admin-breadcrumb">
          <li className="admin-breadcrumb-item">
            <Link to="/admin/dashboard">Dashboard</Link>
          </li>
          <li className="admin-breadcrumb-item">
            <Link to="/admin/membership-plans">Membership Plans</Link>
          </li>
          <li className="admin-breadcrumb-item active">{plan.name}</li>
        </ul>
      </div>
      
      <div className="admin-content-container">
        {/* Plan Summary Header Card */}
        <div className="admin-summary-card" style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '1.5rem',
          backgroundColor: '#fff',
          borderRadius: '8px',
          boxShadow: 'var(--admin-shadow)',
          marginBottom: '1.5rem'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.5rem' }}>
              <h2 style={{ fontSize: '1.5rem', margin: 0 }}>{plan.name}</h2>
              <span style={{
                padding: '0.35rem 0.7rem',
                borderRadius: '20px',
                fontSize: '0.8rem',
                fontWeight: '500',
                textTransform: 'uppercase',
                backgroundColor: plan.status === 'active' ? '#e3fcef' : '#fff0f0',
                color: plan.status === 'active' ? '#0d6832' : '#d63031',
                border: `1px solid ${plan.status === 'active' ? '#a7f3d0' : '#fecaca'}`
              }}>
                {plan.status}
              </span>
            </div>
            <p style={{ 
              margin: '0.5rem 0 0', 
              color: '#6b7280', 
              fontSize: '0.95rem',
              maxWidth: '600px'
            }}>{plan.description}</p>
          </div>
          
          <div className="admin-action-bar" style={{ display: 'flex', gap: '0.75rem' }}>
            <Link
              to={`/admin/membership-plans/${id}/edit`}
              className="admin-btn admin-btn-primary"
              style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: '500'
              }}
            >
              <FontAwesomeIcon icon={faEdit} />
              Edit
            </Link>
            <button
              className="admin-btn"
              onClick={togglePlanStatus}
              style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                backgroundColor: plan.status === 'active' ? '#fff8e1' : '#f0fdf4',
                color: plan.status === 'active' ? '#b45309' : '#166534',
                border: `1px solid ${plan.status === 'active' ? '#fef3c7' : '#dcfce7'}`,
                fontWeight: '500'
              }}
            >
              <FontAwesomeIcon 
                icon={plan.status === 'active' ? faToggleOn : faToggleOff} 
              />
              {plan.status === 'active' ? 'Deactivate' : 'Activate'}
            </button>
            <button
              className="admin-btn admin-btn-danger"
              onClick={handleDelete}
              style={{ 
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontWeight: '500'
              }}
            >
              <FontAwesomeIcon icon={faTrashAlt} />
              Delete
            </button>
          </div>
        </div>
        
        {/* Statistics Cards */}
        <div style={{ 
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: '1.5rem',
          marginBottom: '1.5rem'
        }}>
          <div style={{ 
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '1.5rem',
            boxShadow: 'var(--admin-shadow)',
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem'
          }}>
            <div style={{ 
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#e0f2fe',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FontAwesomeIcon icon={faUsers} size="lg" style={{ color: '#0369a1' }} />
            </div>
            <div>
              <h3 style={{ margin: '0 0 0.25rem', fontSize: '0.9rem', color: '#6b7280' }}>Active Members</h3>
              <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>{stats.activeMembers}</p>
            </div>
          </div>
          
          <div style={{ 
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '1.5rem',
            boxShadow: 'var(--admin-shadow)',
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem'
          }}>
            <div style={{ 
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#f0fdf4',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FontAwesomeIcon icon={faMoneyBillWave} size="lg" style={{ color: '#16a34a' }} />
            </div>
            <div>
              <h3 style={{ margin: '0 0 0.25rem', fontSize: '0.9rem', color: '#6b7280' }}>Total Revenue</h3>
              <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>{formatCurrency(stats.revenue)}</p>
            </div>
          </div>
          
          <div style={{ 
            backgroundColor: '#fff',
            borderRadius: '8px',
            padding: '1.5rem',
            boxShadow: 'var(--admin-shadow)',
            display: 'flex',
            alignItems: 'center',
            gap: '1.5rem'
          }}>
            <div style={{ 
              width: '48px',
              height: '48px',
              borderRadius: '50%',
              backgroundColor: '#fef9c3',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <FontAwesomeIcon icon={faSync} size="lg" style={{ color: '#ca8a04' }} />
            </div>
            <div>
              <h3 style={{ margin: '0 0 0.25rem', fontSize: '0.9rem', color: '#6b7280' }}>Renewal Rate</h3>
              <p style={{ margin: 0, fontSize: '1.5rem', fontWeight: 'bold' }}>{stats.averageRenewalRate}%</p>
            </div>
          </div>
        </div>
      
        {/* Plan Details Card */}
        <div className="admin-card" style={{
          boxShadow: 'var(--admin-shadow)',
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          <div className="admin-card-header" style={{
            padding: '1.25rem 1.5rem',
            borderBottom: '1px solid #e5e7eb',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <h2 className="admin-card-title" style={{ margin: 0, fontSize: '1.25rem' }}>Plan Details</h2>
          </div>
          <div className="admin-card-body" style={{ padding: '1.5rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
              <div>
                <h3 style={{ fontSize: '1rem', margin: '0 0 1rem', fontWeight: '600', color: '#374151' }}>Basic Information</h3>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px'
                  }}>
                    <span style={{ color: '#6b7280', fontWeight: '500' }}>Plan ID</span>
                    <span style={{ fontFamily: 'monospace' }}>{plan.id}</span>
                  </div>
                  <div style={{ 
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: '0.75rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px'
                  }}>
                    <span style={{ color: '#6b7280', fontWeight: '500' }}>Price</span>
                    <span style={{ fontWeight: '600' }}>{formatCurrency(plan.price)}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 style={{ fontSize: '1rem', margin: '0 0 1rem', fontWeight: '600', color: '#374151' }}>Timestamps</h3>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px'
                  }}>
                    <FontAwesomeIcon icon={faClock} style={{ color: '#6b7280' }} />
                    <div>
                      <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Created</div>
                      <div style={{ fontWeight: '500' }}>{formatDate(plan.created_at)}</div>
                    </div>
                  </div>
                  <div style={{ 
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.75rem',
                    padding: '0.75rem',
                    backgroundColor: '#f9fafb',
                    borderRadius: '6px'
                  }}>
                    <FontAwesomeIcon icon={faClock} style={{ color: '#6b7280' }} />
                    <div>
                      <div style={{ color: '#6b7280', fontSize: '0.875rem' }}>Last Updated</div>
                      <div style={{ fontWeight: '500' }}>{formatDate(plan.updated_at)}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <h3 style={{ 
              fontSize: '1rem', 
              margin: '2rem 0 1rem', 
              fontWeight: '600', 
              color: '#374151',
              borderBottom: '1px solid #e5e7eb',
              paddingBottom: '0.5rem'
            }}>Plan Benefits</h3>
            <div style={{ 
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
              gap: '1rem' 
            }}>
              {plan.benefits.map((benefit, index) => (
                <div key={index} style={{ 
                  display: 'flex', 
                  alignItems: 'flex-start',
                  gap: '0.75rem',
                  padding: '0.75rem',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px'
                }}>
                  <div style={{ 
                    backgroundColor: '#e0f2fe',
                    color: '#0369a1',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    marginTop: '2px'
                  }}>
                    <FontAwesomeIcon icon={faCheck} size="xs" />
                  </div>
                  <div style={{ lineHeight: '1.5' }}>{benefit}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal Dialog */}
      {showDeleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1050,
          backdropFilter: 'blur(4px)'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '10px',
            padding: '2rem',
            width: '100%',
            maxWidth: '450px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            animation: 'fadeInUp 0.3s ease-out'
          }}>
            <div style={{ marginBottom: '1.5rem', textAlign: 'center' }}>
              <div style={{ 
                backgroundColor: '#fee2e2', 
                color: '#b91c1c',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem'
              }}>
                <FontAwesomeIcon icon={faTrashAlt} size="lg" />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '600', margin: '0 0 0.5rem' }}>Confirm Deletion</h2>
              <p style={{ margin: 0, color: '#6b7280' }}>
                This action cannot be undone.
              </p>
            </div>
            <p style={{ 
              marginBottom: '1.5rem', 
              padding: '1rem',
              backgroundColor: '#f9fafb',
              borderRadius: '6px',
              textAlign: 'center'
            }}>
              Are you sure you want to delete the <strong>{plan.name}</strong> membership plan?
            </p>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '1rem' 
            }}>
              <button 
                className="admin-btn"
                onClick={cancelDelete}
                style={{ 
                  padding: '0.75rem',
                  backgroundColor: '#f3f4f6', 
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button 
                className="admin-btn"
                onClick={confirmDelete}
                style={{ 
                  padding: '0.75rem',
                  backgroundColor: '#ef4444', 
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Delete Plan
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default ViewMembershipPlan;
